import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsletterForm from '@/components/feature/NewsletterForm';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9pi1cdi2vhsdh2d351g';

export default function About() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formPayload = new FormData(form);
    const honeypot = formPayload.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送！我们会尽快回复您。');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    if (formData.message.length > 500) {
      setFormStatus('error');
      setFormMessage('留言内容不能超过500个字符。');
      return;
    }

    setFormStatus('loading');

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }).toString(),
      });

      const responseText = await response.text();
      let serverMsg = '';
      try {
        const parsed = JSON.parse(responseText);
        serverMsg = parsed?.meta?.message || parsed?.message || '';
      } catch {
        serverMsg = '';
      }

      if (response.ok) {
        setFormStatus('success');
        setFormMessage('消息已发送！感谢您的留言，我们会尽快回复。');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setFormStatus('error');
        setFormMessage(serverMsg || '发送失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  const canonicalUrl = `${window.location.origin}/about`;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51吃瓜网',
    url: window.location.origin,
    description: '51吃瓜网是专注于娱乐热点、明星八卦、社会爆料的综合吃瓜平台。',
    foundingDate: '2024-01',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@51chiagua.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51吃瓜网的内容来源是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51吃瓜网的内容来源于专业的编辑团队和广大网友的投稿爆料。我们拥有资深的娱乐记者团队，同时也接受来自各方的独家爆料投稿，经过核实后发布。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向51吃瓜网投稿爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的"联系我们"表单提交爆料内容，也可以发送邮件至 tips@51chiagua.com。我们会认真评估每一条爆料线索，一经采纳将给予相应报酬。',
        },
      },
      {
        '@type': 'Question',
        name: '51吃瓜网的内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51吃瓜网实行全天候实时更新机制。我们的编辑团队7×24小时追踪热点事件，确保您能在第一时间获取最新吃瓜资讯。',
        },
      },
      {
        '@type': 'Question',
        name: '51吃瓜网如何保证内容的真实性？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们对所有发布内容实行三级审核制度：编辑初审、主编复核、终审发布。对于爆料类内容，我们坚持"多方核实、谨慎发布"的原则，力求在速度与准确性之间取得平衡。',
        },
      },
    ],
  };

  const testimonials = [
    {
      name: '吃瓜路人甲',
      comment: '自从关注了51吃瓜网，再也不怕错过任何娱乐大瓜了！更新速度真的快，编辑部太拼了。',
      rating: 5,
    },
    {
      name: '娱乐圈观察家',
      comment: '内容质量在同类平台中算很不错的，爆料有深度，不是那种标题党。希望继续保持！',
      rating: 5,
    },
    {
      name: '八卦小达人',
      comment: '独家瓜料频道真的绝了，很多料在别的地方根本看不到。你们的线人太强了！',
      rating: 5,
    },
  ];

  return (
    <>
      <title>关于我们 - 51吃瓜网 | 品牌介绍与联系方式</title>
      <meta name="description" content="了解51吃瓜网——专注于娱乐热点、明星八卦、社会爆料的综合吃瓜平台。查看品牌介绍、用户口碑评价，以及联系我们获取更多信息。" />
      <meta name="keywords" content="51吃瓜网,关于我们,品牌介绍,联系方式,吃瓜爆料平台" />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content="关于我们 - 51吃瓜网" />
      <meta property="og:description" content="了解51吃瓜网品牌故事与联系方式。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta name="twitter:card" content="summary" />

      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mb-14" aria-label="品牌介绍">
            <div className="max-w-3xl">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-4">
                关于51吃瓜网
              </h1>
              <div className="w-16 h-1 bg-primary-500 rounded-full mb-6"></div>
              <p className="text-foreground-700 leading-relaxed mb-4">
                <strong>51吃瓜网</strong>创立于2024年，是国内领先的综合吃瓜内容平台。我们专注于娱乐热点、明星八卦、社会爆料等领域，致力于为广大网民提供最新、最快、最全的吃瓜资讯。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                平台汇聚了一支经验丰富的编辑团队和遍布圈内的爆料线人网络，确保内容的时效性和独家性。我们坚持"真实、客观、有趣"的内容理念，力求在娱乐性与真实性之间找到最佳平衡。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                截至目前，51吃瓜网已累计发布超过<strong>10,000篇</strong>原创内容，月活跃用户超过<strong>500万</strong>，是当之无愧的"吃瓜群众聚集地"。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-article-line text-primary-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-2xl font-heading text-primary-500">10,000+</p>
                <p className="text-sm text-foreground-600 mt-1">原创文章</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-full">
                  <i className="ri-user-smile-line text-accent-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-2xl font-heading text-accent-500">500万+</p>
                <p className="text-sm text-foreground-600 mt-1">月活用户</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-team-line text-secondary-500 w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-2xl font-heading text-secondary-500">50+</p>
                <p className="text-sm text-foreground-600 mt-1">编辑团队</p>
              </div>
            </div>
          </section>

          <section id="reputation" className="mb-14" aria-label="用户口碑">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">
              <a href="#reputation">用户口碑</a>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.comment}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">
                    — {t.name}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14" aria-label="常见问题">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">
              常见问题
            </h2>
            <div className="max-w-3xl space-y-3">
              {faqSchema.mainEntity.map((item, idx) => (
                <details
                  key={idx}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                    <h4 className="text-sm font-medium text-foreground-900 pr-4">
                      {item.name}
                    </h4>
                    <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-500 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.acceptedAnswer.text}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section id="contact" className="mb-14" aria-label="联系我们">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">
              <a href="#contact">联系我们</a>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如果您有任何爆料线索、合作意向或其他问题，欢迎通过以下方式联系我们，或直接填写右侧表单。我们的团队会在24小时内回复您。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-full shrink-0">
                      <i className="ri-mail-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm text-foreground-900">contact@51chiagua.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-full shrink-0">
                      <i className="ri-lightbulb-line text-accent-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">爆料投稿</p>
                      <p className="text-sm text-foreground-900">tips@51chiagua.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full shrink-0">
                      <i className="ri-shake-hands-line text-secondary-500 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">商务合作</p>
                      <p className="text-sm text-foreground-900">biz@51chiagua.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      您的姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      电子邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  <div className="honeypot-wrapper">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                        发送中
                      </span>
                    ) : (
                      '发送消息'
                    )}
                  </button>
                  {formStatus !== 'idle' && (
                    <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-600' : 'text-primary-600'}`}>
                      {formMessage}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </section>

          <section className="bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 py-14" aria-label="Newsletter 订阅">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                <i className="ri-mail-send-line text-accent-500 w-6 h-6 flex items-center justify-center"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-3">
                不错过任何精彩瓜
              </h2>
              <p className="text-foreground-600 text-sm mb-6 max-w-md mx-auto">
                订阅51吃瓜网，最新热点爆料第一时间直达您的邮箱。
              </p>
              <NewsletterForm />
            </div>
          </section>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}